import omit from 'lodash/omit';
import pick from 'lodash/pick';

/**
 * Filter menu: multi-screen menu to select a cohort or property filter
 */
import {getPrefixForBehavior} from 'common/query-builder/query-entry-section/row-types/behavior/utils';
import {serializeAttributes} from 'common/util';
import {pluralize} from 'common/util/string';
import {preventTracking, propagateEvent} from 'common/util/dom';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {DisplayContext, ListOperator, NthTimeOperator} from 'common/widgets/property-filter-menu/models/types';
import {isFirstTimeEventFilter, PropertyFilterFactory} from 'common/widgets/property-filter-menu/models/utils';
import {MappedPropertyValues, PropertyType, ResourceType} from 'common/report/constants';
import {cohortsStore} from 'common/report/stores/cohorts';
import {syncReportPropertyValueMappings} from 'common/report/util/mappings';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {isPersistedCustomProperty} from 'common/widgets/custom-property-modal/util';
import {propertiesUtils} from 'common/report/util/properties';
import {queryBuilderUtils} from 'common/query-builder/query-builder/util';
import {getPropertyPath, getPropertyPathPrefix} from 'common/query-builder/query-block/utils/property-util';

import {ChildMenu} from '../child-menu';
import {PropertySectionItem} from '../properties-menu/item-menu-types';
import {PropertiesMenu} from '../properties-menu/index';
import {CohortSegment} from '../segment-menu/types';
import {getCohortFromPropertyItem} from '../utils/util';
import {isProperty, MenuItemType, Property} from '../properties-menu/types';
import {getSelectedPropertyLabel} from '../properties-menu/util';
import {FilterMenuParams, FilterMenuState} from './types';
import css from './index.inline.styl';
import {template} from './template';
import {generateFilterTrackingProps, transformFilterToPropertyItem} from './util';

// Terminology may not be/remain the same as Cohort operators so we remap
// the labels
const COHORT_FILTER_OPERATOR_LABELS = {
  [ListOperator.Contains]: `in`,
  [ListOperator.DoesNotContain]: `not in`,
};

const LOADING_NOTICE = `Loading...`;
// hack to defer rendering in dev and prod of the properties menu to try and increase query-builder
// render speed
const alwaysRender = ![`mixpanel.com`, `mixpanel.org`].some((host) => window.location.host.includes(host));

// based on if the state variable stagedPropertyFilter is not, filter-menu can have one of the 2
// kinds of child nodes underneath: if stagedPropertyFilter is not set, we have properties-menu, for
// the user to pick a property, which gets set to stagedPropertyFilter, which pushes filter-menu to
// the next stage, with mp-property-values-screen as the child. we need to make sure properties-menu
// is the one under the filter-menu instance at all times except only when the filter-menu is open
// and the user has selected a property. If properties-menu is not the child not when the
// filter-menu is closed, for example, we miss any callbacks going to the properties-menu to update
// the state, leaving an inconsistent UI.
// TODO: the above makes the state keeping of filter-menu subtle and error-prone. Can we do better?
// Is having properties-menu always in the DOM (but not always visible) a viable option?
// TODO(chi): add tests for the different consistency cases of custom properties

export class FilterMenu extends ChildMenu<FilterMenuState & {alwaysRender: boolean}, any, any, FilterMenuParams> {
  private propertiesMenu: PropertiesMenu;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'enable-enhanced-list-filters': `boolean`,
      'enable-workspace-filter-restrictions': `boolean`,
      'disable-value-selection': {
        type: `boolean`,
        description: `when set, only the property selection screen is presented`,
      },
      'include-custom-properties': {
        type: `string`,
        enum: [`none`, `persisted-only`, `persisted-non-behavioral-only`, `all`],
        default: `none`,
        description: `whether to hide custom properties from properties menus`,
      },
      'display-context': {type: `string`, default: DisplayContext.Default},
      'disabled-items': {
        type: `json`,
        default: {}, // dict for fast inclusion lookups
        description: `sets disabled options for the underlying mp-items-menu`,
      },
      'scd-poc-enabled': `boolean`,
      'other-filter-options': {
        type: `json`,
        default: `[]`,
        description: `enables filter by other keys`,
      },
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,

      defaultState: {
        dataGroups: [],
        selectedCohortFilterOption: null,
        selectedFilterType: null,
        stagedPropertyFilter: null,
        syncedPropertyValueMappings: false,
        alwaysRender,
      },
      params: {
        listProperty: Object,
      },
      helpers: {
        ...super.config.helpers,
        serializeAttributes,
        preventTracking,
        getSelectedPropertyFilter: () => this.getSelectedPropertyFilter(),
        isCohortSelection: () => this.isCohortSelection(),
        onCohortFilterCommit: () => this.onCohortFilterSelected(),
        onMenuInserted: (vnode) => {
          requestAnimationFrame(() => {
            this.propertiesMenu = vnode.elm;
            // Trigger a render for items dependent on propertiesMenu
            this.update();
          });
        },
        onPropertyFilterBackClick: () => this.update({stagedPropertyFilter: null}),
        onPropertyFilterChange: (ev) => this.update({stagedPropertyFilter: ev.detail.propertyFilter}),
        onPropertyFilterCommit: (ev) => this.onPropertyFilterSelected(ev.detail.propertyFilter),
        /** Updates the staged cohort filter operator */
        onCohortFilterOptionChange: (operator: ListOperator) => this.update({selectedCohortFilterOption: operator}),

        propagateEvent: (ev) => propagateEvent(this, ev),

        /** Handles tab change from underlying mp-properties-menu */
        handlePropertySelectOption: () => {
          let {selectedCohortFilterOption} = this.state;
          if (this.state.stagedPropertyFilter) {
            /** Triggers multi-select */
            selectedCohortFilterOption = ListOperator.Contains;
          }
          /** Always ensure update call on tab change */
          this.update({selectedCohortFilterOption});
        },

        handleStageChange: () => this.update(),
        handleTabChange: () => this.update(),

        /** Stages the selected property or cohort */
        handlePropertySelect: (ev) => {
          const {item} = ev.detail;
          const isCohort = Array.isArray(item) || item.resourceType === ResourceType.Cohort;

          // clear staged property filter if no cohorts are selected
          if (isCohort) {
            if (Array.isArray(item) && !item.length) {
              this.update({stagedPropertyFilter: null});
            } else {
              this.handleCohortSelect(item, {...ev.detail});
            }
          } else {
            this.handlePropertySelect(item, {...ev.detail});
          }
        },

        /** Provides a selection value for the underlying menu */
        getMenuSelection: () => transformFilterToPropertyItem(this.selected),
      },
    };
  }

  public connectedCallback() {
    super.connectedCallback();
    this.fetchCohorts();
    this.fetchDataGroups();
    this.syncPropertyValueMappings();
    const deferredRender = setTimeout(() => this.update({alwaysRender: true}), Math.random() * 500 + 1000);
    this.onDisconnected(() => {
      clearTimeout(deferredRender);
    });
  }

  public attributeChangedCallback(attr, oldVal, newVal) {
    super.attributeChangedCallback(attr, oldVal, newVal);

    if (attr === `open`) {
      if (!this.attr(`open`) || (this.propertiesMenu && this.propertiesMenu.hasOpenedModal())) {
        this.update(
          omit(this.config.defaultState, `cohorts`, `dataGroups`, `syncedPropertyValueMappings`, `alwaysRender`),
        );
      } else {
        this.updateFromAttributes();
      }
    }
  }

  get selected() {
    return this.attr(`open`) ? this.state.stagedPropertyFilter : this.state.selected;
  }

  public shouldCloseOnSelect(detail: any) {
    return !detail.keepMenuOpen;
  }

  /**
   * Sets the selection state from the selected filter
   */
  public updateFromAttributes() {
    const selected = this.attr(`selected`) || {};
    const selectionType = selected && selected.resourceType;
    const isCohort = selectionType === ResourceType.Cohort;
    const selectedCohortFilterOption = isCohort ? selected.filterOperator : null;

    this.update({
      selectedCohortFilterOption,
      stagedPropertyFilter: selectionType ? new PropertyFilter(selected) : null,
    });
  }

  public transformSelectedVal(val) {
    return (val && new PropertyFilter(val)) || val;
  }

  public selectedLeftIcon() {
    const {selected} = this.state;

    switch (selected && selected.resourceType) {
      case ResourceType.User:
        return selected.dataGroupId ? `group-attribute` : `profile-attribute`;
      case ResourceType.Event:
        return `event-attribute`;
      case ResourceType.Cohort:
        return `community`;
      default:
        return null;
    }
  }

  public isUnsavedCustomEntity() {
    const {selected} = this.state;

    switch (selected?.resourceType) {
      case ResourceType.Cohort:
        //If there's a raw_cohort property then this is unsaved
        return Boolean(selected.filterValue[0].cohort.raw_cohort);
      case ResourceType.User:
      case ResourceType.Event: {
        //If customPropertyId is a string with temp then it's not saved
        const customPropId = selected.customPropertyId;
        return customPropId === undefined ? false : !isPersistedCustomProperty(customPropId);
      }
      default:
        return null;
    }
  }

  public getPrefixLabel() {
    if (this.selected && `behavior` in this.selected) {
      return getPrefixForBehavior(this.selected.behavior);
    } else if (this.selected?.resourceType !== ResourceType.Cohort) {
      return getPropertyPathPrefix(transformFilterToPropertyItem(this.selected) as Property, this.state.dataGroups, {
        includeResourceLabel: Boolean(this.params.listProperty) === false && queryBuilderUtils.isCoreReport(),
      });
    }

    return null;
  }

  public selectedLabel() {
    if (this.selected) {
      if (this.selected.filterOperator === NthTimeOperator.NthTime) {
        return `First time ever`;
      }

      if (
        Object.values(MappedPropertyValues).includes(this.selected.propertyName) &&
        !this.state.syncedPropertyValueMappings
      ) {
        return LOADING_NOTICE;
      }

      switch (this.selected.resourceType) {
        case ResourceType.Cohort:
          return this.getCohortFilterLabel(this.selected);
        case ResourceType.User:
        case ResourceType.Event:
        case ResourceType.Other:
          return this.getPropertyFilterLabel(this.selected);
        default:
          break;
      }
    }

    return `Select property...`;
  }

  private getCohortFilterLabel(filter) {
    const {filterValue, filterOperator} = filter;
    const cohortCount = filterValue?.length;

    if (!cohortCount || this.attr(`open`)) {
      return `${this.getCohortFilterOption(filterOperator)}...`;
    }

    const firstCohort = filter.filterValue[0].cohort;
    let cohortName = firstCohort.name;

    // for saved cohorts, prefer using their canonical name from the cohort store
    if (Number.isInteger(firstCohort.id)) {
      const cohort = cohortsStore.value[firstCohort.id];
      cohortName = cohort?.name || firstCohort.name || LOADING_NOTICE;
    }

    const suffix = [
      cohortName,
      cohortCount > 1 ? ` or ${cohortCount - 1} ${pluralize(`other`, cohortCount - 1)}` : ``,
    ].join(``);

    const groupLabel = this.getCohortGroupLabel(this.attr(`data-group-id`));
    return `${groupLabel} ${COHORT_FILTER_OPERATOR_LABELS[filterOperator] || filterOperator} ${suffix}`;
  }

  private getPropertyFilterLabel(filter: PropertyFilter) {
    const propertyItem = transformFilterToPropertyItem(filter) as Property;

    // property selection mode only displays the property name
    if (this.attr(`disable-value-selection`) && filter.resourceType !== ResourceType.Cohort) {
      return getSelectedPropertyLabel({
        selected: propertyItem,
        dataGroupId: propertyItem?.dataGroupId,
      });
    }

    const [, operator, ...labelParts] = filter.getLabelParts();
    let filterValue = [];
    if (!this.attr(`open`)) {
      filterValue = labelParts.map((part) => part.label);
    } else {
      filterValue = [`...`];
    }

    const {name} = getPropertyPath(propertyItem, this.state.dataGroups);
    const propertyName = propertiesUtils.renameProperty(name, filter.resourceType);

    return [propertyName, operator?.label, ...filterValue].filter(Boolean).join(` `);
  }

  private handlePropertySelect(
    item: PropertySectionItem,
    {
      onItemClicked = false,
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      onItemClicked?: boolean;
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    } = {},
  ) {
    const {value} = item;
    let behavior,
      customProperty,
      customPropertyId,
      dataGroupId,
      tempDataGroupId,
      joinPropertyType,
      propertyDefaultType,
      propertyObjectKey,
      type,
      scdEvent;
    if (isProperty(item)) {
      ({
        customProperty,
        customPropertyId,
        dataGroupId,
        tempDataGroupId,
        joinPropertyType,
        propertyDefaultType,
        propertyObjectKey,
        type,
        behavior,
        scdEvent,
      } = item);
    }

    const resourceType =
      item.value === `$multi_attribution_filter`
        ? // multi-attribution filter will have "other" resource type, but filter values will be for the attributed by
          // event property, so needs to be "event" resource type for the PropertyFilter
          ResourceType.Event
        : item.resourceType;

    // not all custom properties have a well-defined type because the formula could return
    // mixed-typed values. Use 'string' for those cases to hydrate the rest of the filter ui
    const {name, propertyType} = customProperty || {name: null, propertyType: PropertyType.String};
    const propertyFilterAttrs = {
      ...(customProperty ? {customProperty} : {}),
      ...(customPropertyId ? {customPropertyId} : {}),
      ...(dataGroupId ? {dataGroupId} : {}),
      ...(tempDataGroupId ? {tempDataGroupId} : {}),
      resourceType,
      propertyDefaultType: propertyDefaultType || propertyType,
      propertyName: value || name,
      ...(behavior ? {behavior} : {}),
      propertyType: type || propertyType,
      ...(this.attr(`scd-poc-enabled`) ? {scdEvent} : {}),
      ...(joinPropertyType ? {joinPropertyType} : {}),
      ...(propertyObjectKey ? {propertyObjectKey} : {}),
      ...(scdEvent ? {scdEvent} : {}),
    };

    if (this.attr(`open`)) {
      // only when we are open, do we set stagedPropertyFilter, to render mp-property-values-screen
      const stagedPropertyFilter = this.createPropertyFilter(propertyFilterAttrs);
      this.update({stagedPropertyFilter});

      // property selection mode dispatches the constructued Partial<PropertyFilter>
      if (this.attr(`disable-value-selection`)) {
        this.dispatchSelect(stagedPropertyFilter, {autoTriggered, menuItemType, stagedItemsStack});
      }
    }
    // we keep the new property (from properties-menu), merge with the values of the old
    // filter (from mp-property-values-screen), add a typecast to the new customProperty filter if
    // the type is different so it fits with the old filter values, to form a filter to bubble up.
    // when we get a *customProperty* *update* from the underlying properties-menu
    if (this.state.selected && (customProperty || customPropertyId) && !onItemClicked) {
      const currentPropertyFilter = this.createPropertyFilter({
        ...propertyFilterAttrs,
        ...{propertyType: this.state.selected.propertyType},
        ...pick(this.state.selected, [
          `propertyObjectKey`,
          `joinPropertyType`,
          `filterOperator`,
          `filterValue`,
          `listQuantifier`,
          `scdEvent`,
        ]),
      });

      this.dispatchSelect(currentPropertyFilter, {
        rawItem: null,
        keepMenuOpen: true,
        autoTriggered,
        menuItemType,
        stagedItemsStack,
      });
    }
  }

  private handleCohortSelect(
    selection: PropertySectionItem | PropertySectionItem[],
    {
      rawItem,
      commit = false,
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      rawItem: PropertySectionItem;
      commit: boolean;
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    },
  ) {
    const isSingleSelection = !Array.isArray(selection);
    const items = isSingleSelection ? [selection] : selection;

    const filterValue = items.map(getCohortFromPropertyItem);

    const filter = this.createCohortFilter({
      filterValue,
    });

    // when selecting a cohort item from the `All` tab
    // or inline cohort commit that filter selection
    if (isSingleSelection || commit || autoTriggered) {
      this.dispatchSelect(filter.getAttributes(), {rawItem, autoTriggered, stagedItemsStack, menuItemType});
    } else {
      this.update({stagedPropertyFilter: filter});
    }
  }

  private dispatchSelect(
    item,
    {
      rawItem = null,
      keepMenuOpen = false,
      autoTriggered = false,
      menuItemType = null,
      stagedItemsStack = null,
    }: {
      rawItem?: PropertySectionItem;
      keepMenuOpen?: boolean;
      autoTriggered?: boolean;
      menuItemType?: MenuItemType;
      stagedItemsStack?: PropertySectionItem[];
    } = {},
  ) {
    const trackingProps = this.trackingProps(item, rawItem);

    let queryReason;
    if (autoTriggered) {
      queryReason = QueryReason.QbAutoTriggered;
    } else {
      queryReason = QueryReason.QbFilterUpdate;
    }

    const detail = {item, trackingProps, keepMenuOpen, queryReason, stagedItemsStack, menuItemType};
    this.dispatchEvent(new CustomEvent(`select`, {detail}));
  }

  private onPropertyFilterSelected(filter: PropertyFilter) {
    this.dispatchSelect(filter.getAttributes());
  }

  private onCohortFilterSelected() {
    const {stagedPropertyFilter} = this.state;

    const filter = this.createCohortFilter({
      ...stagedPropertyFilter.getAttributes(),
    });

    this.dispatchSelect(filter.getAttributes());
  }

  private getSelectedPropertyFilter() {
    const {stagedPropertyFilter} = this.state;
    return stagedPropertyFilter?.propertyName && stagedPropertyFilter?.resourceType !== ResourceType.Cohort;
  }

  private async syncPropertyValueMappings() {
    if (!this.sourceAttrs.projectId) {
      return;
    }
    await syncReportPropertyValueMappings(this.sourceAttrs.projectId);
    this.update({syncedPropertyValueMappings: true});
  }

  private async fetchCohorts() {
    if (!this.attr(`project-has-cohorts`)) {
      return;
    }
    await cohortsStore.fetch({
      projectId: this.sourceAttrs.projectId,
      workspaceId: this.attr(`workspace-id`),
    });
  }

  private async fetchDataGroups() {
    if (!this.attr(`all-data-groups`)) {
      return;
    }
    const dataGroups = await this.retrieveDataGroups();
    this.update({dataGroups});
  }

  private isCohortSelection(): boolean {
    return Boolean(
      this.propertiesMenu?.getSelectedTab() === ResourceType.Cohort ||
        this.propertiesMenu?.state.selectedFilter?.resourceType === ResourceType.Cohort,
    );
  }

  private createPropertyFilter(attrs) {
    const displayContext = this.attr(`display-context`);
    return PropertyFilterFactory.createPropertyFilter(attrs, {displayContext});
  }

  private createCohortFilter(attrs) {
    const {selectedCohortFilterOption} = this.state;

    const filterOperator = selectedCohortFilterOption || this.attr(`selected`)?.filterOperator;

    return this.createPropertyFilter({
      ...attrs,
      filterOperator: filterOperator ?? ListOperator.Contains,
      propertyType: PropertyType.List,
      resourceType: ResourceType.Cohort,
    });
  }

  private getCohortFilterOption(operator: ListOperator) {
    const dataGroupId = this.attr(`data-group-id`);
    const groupLabel = this.getCohortGroupLabel(dataGroupId);
    return `${groupLabel} ${COHORT_FILTER_OPERATOR_LABELS[operator]}`;
  }

  private getCohortGroupLabel(dataGroupId: string) {
    if (!dataGroupId) {
      return `Users`;
    } else {
      const dataGroup = this.state.dataGroups.find((group) => group.groupID === dataGroupId);
      return dataGroup ? `${dataGroup.displayName}(s)` : `Groups`;
    }
  }

  private trackingProps(item: PropertyFilter, rawItem: PropertySectionItem) {
    let extraProps = {};
    const isCohort = item.resourceType === ResourceType.Cohort;

    if (isCohort) {
      const cohortNames = (item.filterValue as CohortSegment[]).map((filter) => filter.cohort.name);
      extraProps = {
        // append cohort metadata
        ...(rawItem
          ? {
              'cohort type': rawItem.createdBy
                ? rawItem.createdBy.id === this.attr(`user-id`)
                  ? `cohorts-yours`
                  : `cohorts`
                : `inline cohorts`,
            }
          : {}),
        'Filter value': cohortNames,
      };
    }

    return generateFilterTrackingProps(item, extraProps);
  }

  getSelectedHoverTooltipData() {
    return this.propertiesMenu && this.propertiesMenu.getSelectedHoverTooltipData();
  }

  getSelectedTrackingProps() {
    return this.propertiesMenu && this.propertiesMenu.getSelectedTrackingProps();
  }

  handleTooltipContextAction(ev: Event) {
    return this.propertiesMenu && this.propertiesMenu.handleTooltipContextAction(ev);
  }

  handleCloseMenu() {
    this.propertiesMenu && this.propertiesMenu.handleCloseMenu();
  }

  canOpenMenu() {
    return this.selected && super.canOpenMenu() ? !isFirstTimeEventFilter(this.selected) : true;
  }
}

export const FilterMenuEl = defineAndMakeJsxFactory(`mp-filter-menu`, FilterMenu);
export const FilterMenuReact = panel2react(`mp-filter-menu`, FilterMenu);
